'use client'

import useTimeout from '@/hooks/use-timeout'
import generateClassNames from '@/utils/generate-class-names'
import clsx from 'clsx'
import mergeRefs from 'merge-refs'
import React, { forwardRef, useRef } from 'react'
import { Transition } from 'react-transition-group'
import './collapse.scss'

const classes = generateClassNames('collapse', ['wrapper', 'inner'])

const getTransitionDuration = (height: number) => {
  if (!height) {
    return 0
  }

  const constant = height / 80

  // https://www.wolframalpha.com/input/?i=(4+%2B+15+*+(x+%2F+36+)+**+0.25+%2B+(x+%2F+36)+%2F+5)+*+10
  return Math.round((4 + 15 * constant ** 0.25 + constant / 5) * 10)
}

export interface CollapseProps extends React.HTMLAttributes<HTMLDivElement> {
  in?: boolean
  appear?: boolean
}

const Collapse = forwardRef(function Collapse(
  props: CollapseProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const { appear, children, className, in: inProp, ...other } = props

  const nodeRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const transitionDuration = useRef<number>()

  const timer = useTimeout()

  const getWrapperSize = () =>
    wrapperRef.current ? wrapperRef.current.clientHeight : 0

  const handleEnter = () => {
    const node = nodeRef.current

    if (node) {
      node.style.height = '0px'
      node.style.opacity = '0'
    }
  }

  const handleEntering = () => {
    const node = nodeRef.current

    if (node) {
      const wrapperSize = getWrapperSize()
      const duration = getTransitionDuration(wrapperSize)
      transitionDuration.current = duration
      node.style.transitionDuration = `${duration}ms`
      node.style.height = `${wrapperSize}px`
      node.style.opacity = '1'
    }
  }

  const handleEntered = () => {
    const node = nodeRef.current

    if (node) {
      node.style.height = 'auto'
      node.style.opacity = '1'
    }
  }

  const handleExit = () => {
    const node = nodeRef.current

    if (node) {
      node.style.height = `${getWrapperSize()}px`
      node.style.opacity = '1'
    }
  }

  const handleExiting = () => {
    const node = nodeRef.current

    if (node) {
      const wrapperSize = getWrapperSize()
      const duration = getTransitionDuration(wrapperSize)
      transitionDuration.current = duration
      node.style.transitionDuration = `${duration}ms`
      node.style.height = '0px'
      node.style.opacity = '0'
    }
  }

  const handleAddEndListener = (done: () => void) => {
    timer.start(transitionDuration.current || 0, done)
  }

  return (
    <Transition
      addEndListener={handleAddEndListener}
      appear={appear}
      in={inProp}
      nodeRef={nodeRef}
      onEnter={handleEnter}
      onEntered={handleEntered}
      onEntering={handleEntering}
      onExit={handleExit}
      onExiting={handleExiting}
    >
      {(status) => (
        <div
          ref={mergeRefs(nodeRef, ref)}
          className={clsx(
            classes.root,
            {
              [`${classes.root}--collapsed`]: status === 'exited' && !inProp,
            },
            className
          )}
          {...other}
        >
          <div ref={wrapperRef} className={classes.wrapper}>
            {children}
          </div>
        </div>
      )}
    </Transition>
  )
})

export default Collapse
